import React, { useState } from "react";

import { useShopifyProduct } from "@app/scripts/react-hooks/shopify";
import { ProductMoreColorsPlp } from "@app/scripts/react-product-more-colors/components/ProductMoreColorsPlp";
import { QuickAdd } from "@app/scripts/react-quick-add/components/QuickAdd";
import { QuickAddPlp } from "@app/scripts/react-quick-add/components/QuickAddPlp";
import { ProductPrice } from "@app/scripts/react-product/components/ProductPrice";
import { ProductSizeSelect } from "@app/scripts/react-product-details/components/ProductSizeSelect";
import config from "@app/config";
import { useIsMobile } from "@app/scripts/react-hooks/isMobile";

import type { NormalisedProduct } from "@app/scripts/types";

type Props = {
  product: NormalisedProduct;
  additionalClass?: string;
  hasQuickAddDrawer?: boolean;
  quickAddStyle?: "default" | "pin" | "plp";
  addToCartBtn?: boolean;
  addAsGift?: boolean;
};

export const ProductCard: React.FC<Props> = ({
  product,
  additionalClass,
  hasQuickAddDrawer = true,
  quickAddStyle = "default",
  addToCartBtn = false,
  addAsGift = false,
}) => {
  const { color, badges, url, title, handleChangeVariant, handleAddToCart, selectedVariantId, error } = useShopifyProduct({ product });
  const [isHovered, setIshovered] = useState(false);
  const [related, setRelated] = useState<NormalisedProduct[]>([]); // limit to 4 products
  const isMobile = useIsMobile();
  const submitTitle = isMobile ? "Add" : "Add to Cart";

  return (
    <article className={`product-card ${additionalClass ?? ""} ${!related.length ? "no-related" : ""}`}>
      <a
        href={url}
        className="product-card__img-wrapper relative"
        onMouseEnter={() => setIshovered(true)}
        onMouseLeave={() => setIshovered(false)}
      >
        <div className="product-card__img">
          {product.media?.[0] && (
            <img className="motion-reduce" src={product.media[0].src} alt={product.media[0].alt} />
          )}
          {product.media?.[1] && (
            <img
              className={`secondary-image ${isHovered ? "active" : ""}`}
              src={product.media[1].src}
              alt={product.media[1].alt}
            />
          )}
        </div>

        {(quickAddStyle === "pin" || quickAddStyle === "plp") && hasQuickAddDrawer && !addAsGift && (
          <QuickAdd product={product} btnStyle="pin" className={quickAddStyle === "plp" ? "hide-desktop" : ""} />
        )}
        {quickAddStyle === "plp" && hasQuickAddDrawer && isHovered && !addAsGift && (
          <QuickAddPlp product={product} className="hide-mobile" />
        )}
      </a>

      <div className="product-card__content">
        <a href={url} className={`full-unstyled-link product-card__title`}>
          <h3>{title}</h3>
        </a>

        <ProductMoreColorsPlp product={product} related={related} setRelated={setRelated} />

        <div className="product-card__price">
          <ProductPrice product={product} />
        </div>

        <span className="product-card__color">{color}</span>

        {badges && badges.length > 0 ? (
          <div className="product-card__badges">
            {badges.map((badge) => (
              <span className="badge" data-flagcolor={badge.tag}>
                {badge.title.replaceAll("-", " ")}
              </span>
            ))}
          </div>
        ) : (
          ""
        )}

        {quickAddStyle === "default" && hasQuickAddDrawer && (
          <div className="product-card__quick-add">
            <QuickAdd product={product} btnStyle="default" />
          </div>
        )}
        {addToCartBtn && (
          <div className="product-card__add-to-cart gwp-add-to-cart">
            <ProductSizeSelect handleChangeVariant={handleChangeVariant} variants={product?.variants} />
            <button
              className={`button button--small button--secondary swiper-no-swiping`}
              onClick={(e) =>
                handleAddToCart(
                  e,
                  selectedVariantId,
                  addAsGift
                    ? {
                      [config?.attributes?.isGwp]: "true",
                    }
                    : null
                )
              }
            >
              {submitTitle}
            </button>
            {error && (
              <small className="product-card__add-to-cart__error bg-brand-error text-brand-white">{error}</small>
            )}
          </div>
        )}
      </div>
    </article>
  );
};
